<template>
  <div class="settings-shell">
    <nav class="settings-rail" aria-label="Settings">
      <template v-for="group in visibleGroups" :key="group.label">
        <span class="settings-rail-group">{{ group.label }}</span>
        <RouterLink
          v-for="item in group.items"
          :key="item.to"
          class="settings-rail-link"
          :to="item.to"
        >
          <component :is="item.icon" class="icon" />
          <span>{{ item.label }}</span>
        </RouterLink>
      </template>
    </nav>
    <div class="settings-content">
      <RouterView />
    </div>
  </div>
</template>

<script setup lang="ts">
import { FolderClosed, Palette, Settings, ShieldCheck, UserRound, Users, Wrench } from 'lucide-vue-next';
import type { Component } from 'vue';
import { computed } from 'vue';
import { RouterLink, RouterView } from 'vue-router';
import { PermissionKind } from '../../api/generated';
import { useSessionStore } from '../../stores/session';

interface NavItem {
  label: string;
  to: string;
  icon: Component;
  visible: () => boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const session = useSessionStore();
const can = (permission: PermissionKind): boolean => session.hasGlobalPermission(permission);

const groups: NavGroup[] = [
  {
    label: 'Personal',
    items: [
      { label: 'Profile', to: '/settings/profile', icon: UserRound, visible: () => true },
      { label: 'Preferences', to: '/settings/appearance', icon: Palette, visible: () => true }
    ]
  },
  {
    label: 'Workspace',
    items: [
      { label: 'General', to: '/settings/general', icon: Settings, visible: () => can(PermissionKind.MANAGE_SETTINGS) },
      {
        label: 'Projects',
        to: '/settings/projects',
        icon: FolderClosed,
        visible: () => can(PermissionKind.CREATE_PROJECT) || can(PermissionKind.MANAGE_PROJECT_ACCESS)
      },
      { label: 'Users', to: '/settings/users', icon: Users, visible: () => can(PermissionKind.MANAGE_USERS) },
      { label: 'Roles', to: '/settings/roles', icon: ShieldCheck, visible: () => can(PermissionKind.MANAGE_ROLES) },
      { label: 'Maintenance', to: '/settings/maintenance', icon: Wrench, visible: () => can(PermissionKind.ADMIN_ACCESS) }
    ]
  }
];

const visibleGroups = computed(() =>
  groups
    .map(group => ({ label: group.label, items: group.items.filter(item => item.visible()) }))
    .filter(group => group.items.length > 0));
</script>
